import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import CollapsableSection from "../../../../../components/CollapsibleSection";
import ButtonForm from "../../../../../components/ButtonForm";
import InputForm from "../../../../../components/InputForm";
import SelectableList from "../../../../../components/SelectableList";
import SelectionForm from "../../../../../components/SelectionForm";
import Spinner from "../../../../../components/Spinner";

import AddIco from "../../../../../components/icons/AddIco";
import CheckIco from "../../../../../components/icons/CheckIco";
import TrashIco from "../../../../../components/icons/TrashIco";
import Toggle from "../../../../../components/forms/Toggle";

import { useAuth } from "../../../../../providers/AuthContext";
import { useAppContext } from "../../../../../providers/AppContext";
import { useDashboards } from "../../../../../providers/DashboardContext";
import { useProcesses } from "../../../../../providers/ProcessesContext";
import { useWidgets } from "../../../../../providers/WidgetContext";
import {
  createWidget,
  getWidgetById,
  updateWidget,
} from "../../../../../services/apiWidgests";
import { getAllParameters } from "../../../../../services/apiProcessParameteres";
import { translateDatePicker } from "../../../../utils";

const DEFAULT_DATE_RANGE = {
  activeTab: "customDates",
  selectedRange: "byDashboard",
};

const AGGREGATION_OPTIONS = {
  add: "Suma",
  mean: "Promedio",
  max: "Maximo",
  min: "Minimo",
  count: "Conteo",
};

const DATA_DISPLAY_OPTIONS = {
  lastValue: "Ultimo valor",
  add: "Suma",
  mean: "Promedio",
  max: "Maximo",
  min: "Minimo",
};

const TIME_OPTIONS = {
  minutes: "Minutos",
  hours: "Horas",
  days: "Dias",
  weaks: "Semanas",
  months: "Meses",
};

const FULL_WIDTH_OPTIONS = {
  yes: "SI",
  no: "NO",
};

const createMetricItem = (metric = {}) => ({
  localId:
    metric.localId || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  name: metric.name || "",
  fullWidth: !!metric.fullWidth,
  process: metric.process,
  valueToCompare: metric.valueToCompare,
  variableToCompare: metric.variableToCompare,
  groupList: metric.groupList,
  idealValue: metric.idealValue,
  isRate: !!metric.isRate,
  dataDisplay: metric.dataDisplay || "lastValue",
  aggregation: metric.aggregation || "add",
  timespan: metric.timespan || "hours",
  interval: metric.interval || 1,
});

const sanitizeMetricForSave = (metric) => ({
  name: metric.name?.trim() || "",
  fullWidth: !!metric.fullWidth,
  process: metric.process,
  valueToCompare: metric.valueToCompare,
  variableToCompare: metric.variableToCompare,
  groupList: metric.groupList,
  idealValue: metric.idealValue,
  isRate: !!metric.isRate,
  dataDisplay: metric.dataDisplay || "lastValue",
  aggregation: metric.aggregation || "add",
  timespan: metric.timespan || "hours",
  interval: Number(metric.interval) || 1,
});

const isMetricComplete = (metric) => {
  if (!metric?.name?.trim()) return false;
  if (!metric?.process?.id) return false;
  if (!metric?.valueToCompare?.key) return false;
  if (!metric?.variableToCompare?.key) return false;
  if (!metric?.dataDisplay) return false;
  if (!metric?.aggregation) return false;
  if (!metric?.timespan) return false;
  if (
    metric?.interval === "" ||
    metric?.interval == null ||
    Number(metric.interval) <= 0
  )
    return false;
  return true;
};

const ColumnField = ({ header, onDelete = () => {} }) => {
  if (!header) return null;

  return (
    <div className="mr-3 flex w-full items-center justify-between">
      <div className="flex basis-1/3 items-center gap-1">
        <span>{header.alias}</span>
      </div>
      <span className="basis-1/3 text-gray-400">{header.key}</span>
      <button type="button" onClick={() => onDelete(header)}>
        <TrashIco fill="red" />
      </button>
    </div>
  );
};

const MetricSection = ({
  metric,
  processList,
  parameters,
  isFetchingParameters,
  isOpen,
  onMetricChange,
  onMetricDelete,
  onProcessChange,
  onOpenStage,
  onToggle,
}) => {
  const renderFieldSection = ({
    field,
    title,
    selectedParameter,
    onDelete,
    stageName,
  }) => (
    <CollapsableSection
      title={title}
      titleBgColor="bg-aliceblue"
      titleTextColor="text-gray"
      open={true}
    >
      <div className="w-full flex flex-col px-4">
        {selectedParameter && (
          <ColumnField header={selectedParameter} onDelete={onDelete} />
        )}
        {!selectedParameter && (
          <div className="mx-auto">
            <ButtonForm
              text="Agregar campo"
              Icon={() => <AddIco fill="white" />}
              enabled={
                !!metric.process?.id &&
                !isFetchingParameters &&
                parameters.length !== 0
              }
              action={() => onOpenStage(metric.localId, stageName)}
            />
          </div>
        )}
      </div>
    </CollapsableSection>
  );

  return (
    <CollapsableSection
      titleBgColor="bg-aliceblue"
      titleTextColor="text-gray"
      CustomTitleBar={() => (
        <div className="flex w-full items-center justify-between gap-3 pr-3">
          <div className="flex min-w-0 flex-col">
            <span className="truncate font-semibold text-gray-800">
              {metric.name?.trim() || "Nuevo proceso"}
            </span>
            <span className="truncate text-xs text-gray-500">
              {metric.process?.name || "Sin proceso"}
            </span>
          </div>
          <button
            type="button"
            className="rounded p-1 hover:bg-red-50"
            onClick={(event) => {
              event.stopPropagation();
              onMetricDelete(metric.localId);
            }}
          >
            <TrashIco fill="red" />
          </button>
        </div>
      )}
      open={true}
      isOpen={isOpen}
      onToggle={onToggle}
    >
      <div className="flex flex-col gap-2">
        <InputForm
          text="Nombre"
          value={metric.name}
          onChange={(event) =>
            onMetricChange(metric.localId, "name", event.target.value)
          }
        />

        <div className="mb-1.5 flex w-[100%] items-center justify-between">
          <SelectionForm
            key={`metric-process-${metric.localId}-${metric.process?.id || "empty"}`}
            text="Proceso"
            options={Object.keys(processList)}
            mask={processList}
            value={metric.process?.id}
            onChange={(value) => onProcessChange(metric.localId, value)}
            AppendClassName=" -mb-4"
            error={
              metric.process?.id &&
              !isFetchingParameters &&
              parameters.length === 0
                ? "El proceso no tiene parametros configurados"
                : ""
            }
          />
          <div className="flex w-full justify-end">
            <Toggle
              key={`metric-toggle-${metric.localId}-${metric.isRate ? "on" : "off"}`}
              label="Calcular tasa"
              initialValue={!!metric.isRate}
              handleChange={(value) =>
                onMetricChange(metric.localId, "isRate", value)
              }
            />
          </div>
        </div>

        {renderFieldSection({
          field: "valueToCompare",
          title: "Valor (datos numericos)",
          selectedParameter: metric.valueToCompare,
          onDelete: () =>
            onMetricChange(metric.localId, "valueToCompare", undefined),
          stageName: "setValueToCompare",
        })}

        {renderFieldSection({
          field: "variableToCompare",
          title: "Variable de tiempo",
          selectedParameter: metric.variableToCompare,
          onDelete: () =>
            onMetricChange(metric.localId, "variableToCompare", undefined),
          stageName: "setTimeValue",
        })}

        {!metric.isRate && (
          <>
            {renderFieldSection({
              field: "groupList",
              title: "Agrupar por",
              selectedParameter: metric.groupList,
              onDelete: () =>
                onMetricChange(metric.localId, "groupList", undefined),
              stageName: "setGroupList",
            })}

            {renderFieldSection({
              field: "idealValue",
              title: "Comparar con",
              selectedParameter: metric.idealValue,
              onDelete: () =>
                onMetricChange(metric.localId, "idealValue", undefined),
              stageName: "setIdealValue",
            })}
          </>
        )}

        {metric.isRate && (
          <>
            <div className="-mb-4 pt-2">
              <SelectionForm
                key={`metric-aggregation-${metric.localId}-${metric.aggregation}`}
                text="Metodo de agregacion"
                options={Object.keys(AGGREGATION_OPTIONS)}
                mask={AGGREGATION_OPTIONS}
                value={metric.aggregation}
                onChange={(value) =>
                  onMetricChange(metric.localId, "aggregation", value)
                }
                AppendClassName=" -mb-4"
              />
            </div>

            <div className="-mb-4 pt-4">
              <div className="flex items-center gap-1">
                <InputForm
                  text="Intervalo"
                  type="number"
                  value={metric.interval}
                  onChange={(event) =>
                    onMetricChange(metric.localId, "interval", event.target.value)
                  }
                  AppendClassName=" -mb-4"
                />

                <SelectionForm
                  key={`metric-timespan-${metric.localId}-${metric.timespan}`}
                  text="Tiempo"
                  options={Object.keys(TIME_OPTIONS)}
                  mask={TIME_OPTIONS}
                  value={metric.timespan}
                  onChange={(value) =>
                    onMetricChange(metric.localId, "timespan", value)
                  }
                  AppendClassName=" -mb-4"
                />
              </div>
            </div>
          </>
        )}

        <div className="pt-2">
          <SelectionForm
            key={`metric-display-${metric.localId}-${metric.dataDisplay}`}
            text="Disposicion de datos"
            options={Object.keys(DATA_DISPLAY_OPTIONS)}
            mask={DATA_DISPLAY_OPTIONS}
            value={metric.dataDisplay}
            onChange={(value) =>
              onMetricChange(metric.localId, "dataDisplay", value)
            }
            AppendClassName=" -mb-4"
          />
        </div>

        <SelectionForm
          key={`metric-full-width-${metric.localId}-${metric.fullWidth ? "yes" : "no"}`}
          text="Ocupar ancho completo"
          options={Object.keys(FULL_WIDTH_OPTIONS)}
          mask={FULL_WIDTH_OPTIONS}
          value={metric.fullWidth ? "yes" : "no"}
          onChange={(value) =>
            onMetricChange(metric.localId, "fullWidth", value === "yes")
          }
        />
      </div>
    </CollapsableSection>
  );
};

const MetricFieldSelectionView = ({ metric, items, onSelect, onClose }) => (
  <div className="flex flex-col gap-2 h-[25rem] relative overflow-auto">
    <div className="flex flex-col flex-grow gap-2">
      <span className="font-semibold text-lg">{metric?.process?.name || ""}</span>
      <SelectableList items={items} multiSelect={false} setItems={onSelect} />
    </div>

    <div className="flex-none sticky bottom-0 bg-white p-2 shadow-lg">
      <div className="flex w-full justify-end">
        <ButtonForm text="Guardar" enabled={true} action={onClose} />
      </div>
    </div>
  </div>
);

function MultiMetricForm({ onSubmit = () => {}, id }) {
  const { auth } = useAuth();
  const { addToast } = useAppContext();
  const navigate = useNavigate();

  const { addCard, assignSpace } = useWidgets();
  const { dashboard, selectedTab } = useDashboards();
  const { menuItems = [] } = useProcesses();

  const [title, setTitle] = useState("");
  const [configuration, setConfiguration] = useState({
    dateRange: DEFAULT_DATE_RANGE,
    nDecimals: 2,
    metrics: [],
  });
  const [dataRangeLabel, setDataRangeLabel] = useState("Asignar por dashboard");
  const [processList, setProcessList] = useState({});
  const [parametersByProcess, setParametersByProcess] = useState({});
  const [fetchingParametersByProcess, setFetchingParametersByProcess] =
    useState({});
  const [fetchingWidgetInfo, setFetchingWidgetInfo] = useState(!!id);
  const [savingWidget, setSavingWidget] = useState(false);
  const [activeMetricStage, setActiveMetricStage] = useState();
  const [metricSectionOpenById, setMetricSectionOpenById] = useState({});

  const validate = useMemo(() => {
    return (
      !!title.trim() &&
      configuration.nDecimals !== "" &&
      configuration.nDecimals != null &&
      Number(configuration.nDecimals) >= 0 &&
      configuration.metrics.length > 0 &&
      configuration.metrics.every(isMetricComplete)
    );
  }, [configuration.metrics, configuration.nDecimals, title]);

  const fetchParameters = async (processId) => {
    if (
      !processId ||
      parametersByProcess[processId] ||
      fetchingParametersByProcess[processId]
    ) {
      return;
    }

    setFetchingParametersByProcess((prev) => ({ ...prev, [processId]: true }));

    try {
      const response = await getAllParameters(processId, auth?.token);
      setParametersByProcess((prev) => ({
        ...prev,
        [processId]: response.map((parameter) => ({
          key: parameter.key,
          alias: parameter.alias,
          type: parameter.datatype,
        })),
      }));
    } catch (error) {
      if (error?.response?.status === 404) {
        addToast("Proceso no encontrado", 3);
      } else if (error?.response?.status === 500) {
        addToast("Error en el servidor, intente nuevamente", 2);
      } else if (error?.response?.status === 419) {
        addToast("Sesion expirada", 1);
        navigate("/logout");
      } else {
        navigate("/logout");
      }
    } finally {
      setFetchingParametersByProcess((prev) => ({
        ...prev,
        [processId]: false,
      }));
    }
  };

  const updateMetric = (localId, field, value) => {
    setConfiguration((prev) => ({
      ...prev,
      metrics: prev.metrics.map((metric) =>
        metric.localId === localId ? { ...metric, [field]: value } : metric,
      ),
    }));
  };

  const handleProcessChange = (localId, processId) => {
    const processName = processList[processId];

    setConfiguration((prev) => ({
      ...prev,
      metrics: prev.metrics.map((metric) =>
        metric.localId === localId
          ? {
              ...metric,
              name:
                !metric.name?.trim() || metric.name === metric.process?.name
                  ? processName
                  : metric.name,
              process: { id: processId, name: processName },
              valueToCompare: undefined,
              variableToCompare: undefined,
              idealValue: undefined,
              groupList: undefined,
            }
          : metric,
      ),
    }));

    fetchParameters(processId);
  };

  const handleOptionalParameterChange = (
    localId,
    field,
    selectedLabel,
    parameterList,
  ) => {
    const selectedParameter = parameterList.find(
      (parameter) =>
        selectedLabel === parameter.alias || selectedLabel === parameter.key,
    );

    updateMetric(localId, field, selectedParameter);
  };

  const handleAddMetric = () => {
    const newMetric = createMetricItem();

    setMetricSectionOpenById((prev) => ({
      ...prev,
      [newMetric.localId]: true,
    }));

    setConfiguration((prev) => ({
      ...prev,
      metrics: [...prev.metrics, newMetric],
    }));
  };

  const handleDeleteMetric = (localId) => {
    if (activeMetricStage?.localId === localId) {
      setActiveMetricStage(undefined);
    }
    setMetricSectionOpenById((prev) => {
      const nextState = { ...prev };
      delete nextState[localId];
      return nextState;
    });
    setConfiguration((prev) => ({
      ...prev,
      metrics: prev.metrics.filter((metric) => metric.localId !== localId),
    }));
  };

  const handleToggleMetricSection = (localId, nextIsOpen) => {
    setMetricSectionOpenById((prev) => ({
      ...prev,
      [localId]: nextIsOpen,
    }));
  };

  const handleOpenMetricStage = (localId, stage) => {
    setActiveMetricStage({ localId, stage });
  };

  const handleDateRangeChange = (dateRange) => {
    setDataRangeLabel(translateDatePicker(dateRange));
    setConfiguration((prev) => ({ ...prev, dateRange }));
  };

  const buildPayload = () => ({
    type: "processMultiMetric",
    title: title.trim(),
    description: "",
    configuration: {
      dateRange: configuration.dateRange,
      nDecimals: Number(configuration.nDecimals ?? 0),
      metrics: configuration.metrics.map(sanitizeMetricForSave),
    },
  });

  const saveNewWidget = async () => {
    const position = assignSpace();
    const payload = buildPayload();

    const response = await createWidget(
      {
        ...payload,
        dashboardId: dashboard._id,
        x: position.x,
        y: position.y,
        width: 200,
        height: 200,
        selectedTab,
      },
      auth?.token,
    );

    addCard(response._id, response.title, response.type, response.dashboardId);
  };

  const saveExistingWidget = async () => {
    const payload = buildPayload();
    await updateWidget(id, payload, auth?.token);
  };

  const handleSubmit = async () => {
    if (!validate) return;

    setSavingWidget(true);

    try {
      if (id) {
        await saveExistingWidget();
        addToast(`Widget ${title.trim()} editado exitosamente.`, 1);
      } else {
        await saveNewWidget();
        addToast(`Widget ${title.trim()} creado exitosamente.`, 1);
      }

      onSubmit();
    } catch (error) {
      if (error?.response?.status === 404) {
        addToast("Proceso no encontrado", 3);
      } else if (error?.response?.status === 500) {
        addToast("Error en el servidor, intente nuevamente", 2);
      } else if (error?.response?.status === 419) {
        addToast("Sesion expirada", 1);
        navigate("/logout");
      } else {
        navigate("/logout");
      }
    } finally {
      setSavingWidget(false);
    }
  };

  useEffect(() => {
    const nextProcessList = {};
    menuItems.slice(1).forEach((item) => {
      nextProcessList[item.id] = item.label;
    });
    setProcessList(nextProcessList);
  }, [menuItems]);

  useEffect(() => {
    if (!id) return;

    const refreshWidgetInfo = async () => {
      try {
        setFetchingWidgetInfo(true);
        const widget = await getWidgetById(id, auth?.token);
        const metrics = Array.isArray(widget?.configuration?.metrics)
          ? widget.configuration.metrics.map((metric) =>
              createMetricItem(metric),
            )
          : [];

        setTitle(widget?.title || "");
        setConfiguration({
          dateRange: widget?.configuration?.dateRange || DEFAULT_DATE_RANGE,
          nDecimals:
            widget?.configuration?.nDecimals ??
            widget?.configuration?.metrics?.[0]?.nDecimals ??
            2,
          metrics,
        });
        setMetricSectionOpenById(
          metrics.reduce((accumulator, metric) => {
            accumulator[metric.localId] = true;
            return accumulator;
          }, {}),
        );
        setDataRangeLabel(
          translateDatePicker(
            widget?.configuration?.dateRange || DEFAULT_DATE_RANGE,
          ),
        );

        metrics.forEach((metric) => {
          if (metric.process?.id) {
            fetchParameters(metric.process.id);
          }
        });
      } catch (error) {
        if (error?.response?.status === 404) {
          addToast("Widget no encontrado", 3);
        } else if (error?.response?.status === 500) {
          addToast("Error en el servidor, intente nuevamente", 2);
        } else {
          navigate("/logout");
        }
      } finally {
        setFetchingWidgetInfo(false);
      }
    };

    refreshWidgetInfo();
  }, [auth?.token, id]);

  if (fetchingWidgetInfo) {
    return <Spinner />;
  }

  const activeMetric = configuration.metrics.find(
    (metric) => metric.localId === activeMetricStage?.localId,
  );

  if (activeMetric && activeMetricStage?.stage) {
    const parameters = parametersByProcess[activeMetric.process?.id] || [];
    const stageConfig = {
      setValueToCompare: {
        items: parameters
          .filter((parameter) => parameter.type === "numeric")
          .map((parameter) => parameter.alias || parameter.key),
        field: "valueToCompare",
        parameterList: parameters.filter(
          (parameter) => parameter.type === "numeric",
        ),
      },
      setTimeValue: {
        items: parameters
          .filter((parameter) => parameter.type === "date")
          .map((parameter) => parameter.alias || parameter.key),
        field: "variableToCompare",
        parameterList: parameters.filter(
          (parameter) => parameter.type === "date",
        ),
      },
      setIdealValue: {
        items: parameters
          .filter((parameter) => parameter.type === "numeric")
          .map((parameter) => parameter.alias || parameter.key),
        field: "idealValue",
        parameterList: parameters.filter(
          (parameter) => parameter.type === "numeric",
        ),
      },
      setGroupList: {
        items: parameters
          .filter((parameter) => !parameter.type)
          .map((parameter) => parameter.alias || parameter.key),
        field: "groupList",
        parameterList: parameters.filter((parameter) => !parameter.type),
      },
    }[activeMetricStage.stage];

    return (
      <MetricFieldSelectionView
        metric={activeMetric}
        items={stageConfig?.items || []}
        onSelect={(selectedItems) =>
          handleOptionalParameterChange(
            activeMetric.localId,
            stageConfig?.field,
            selectedItems,
            stageConfig?.parameterList || [],
          )
        }
        onClose={() => setActiveMetricStage(undefined)}
      />
    );
  }

  return (
    <div className="relative flex h-[35rem] flex-col gap-2 overflow-auto">
      <div className="flex flex-grow flex-col gap-2">
        <CollapsableSection
          title="Datos"
          titleBgColor="bg-lightblue"
          titleTextColor="text-bluesens"
          open={true}
          border={false}
        >
          <div className="flex flex-col gap-3">
            <div className="flex justify-end">
              <ButtonForm
                text="Agregar procesos"
                enabled={true}
                action={handleAddMetric}
              />
            </div>

            {configuration.metrics.length === 0 && (
              <span className="text-sm text-red-500">
                Debe existir al menos un proceso configurado.
              </span>
            )}

            {configuration.metrics.map((metric) => (
              <MetricSection
                key={metric.localId}
                metric={metric}
                processList={processList}
                parameters={parametersByProcess[metric.process?.id] || []}
                isFetchingParameters={
                  !!fetchingParametersByProcess[metric.process?.id]
                }
                isOpen={metricSectionOpenById[metric.localId] ?? true}
                onMetricChange={updateMetric}
                onMetricDelete={handleDeleteMetric}
                onProcessChange={handleProcessChange}
                onOpenStage={handleOpenMetricStage}
                onToggle={(nextIsOpen) =>
                  handleToggleMetricSection(metric.localId, nextIsOpen)
                }
              />
            ))}

            <div className="pt-4">
              <InputForm
                type="datarange"
                text="Rango de tiempo"
                value={dataRangeLabel}
                AppendClassName="text-left"
                onChange={handleDateRangeChange}
                dateRangeOptions={configuration.dateRange}
                addedRanges={{ byDashboard: () => {} }}
              />
            </div>
          </div>
        </CollapsableSection>

        <CollapsableSection
          title="Apariencia"
          titleBgColor="bg-lightblue"
          titleTextColor="text-bluesens"
          open={true}
          border={false}
        >
          <div className="flex flex-col gap-2">
            <InputForm
              text="Nombre"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
            />

            <InputForm
              text="Decimales"
              type="number"
              value={configuration.nDecimals}
              onChange={(event) =>
                setConfiguration((prev) => ({
                  ...prev,
                  nDecimals: event.target.value,
                }))
              }
            />
          </div>
        </CollapsableSection>
      </div>

      <div className="sticky bottom-0 flex-none bg-white p-2 shadow-lg">
        <div className="flex w-full justify-end">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!validate || savingWidget}
          >
            <CheckIco
              width="50px"
              height="50px"
              fill={validate && !savingWidget ? "bluesens" : "gray"}
            />
          </button>
        </div>
      </div>
    </div>
  );
}

export default MultiMetricForm;
